/**
 * The post-import panel: an imported/skipped tally plus, when the server
 * refused rows, a scrollable table naming each one. Shared by every import
 * dialog so the wording of a partial import stays identical everywhere.
 */
import type { ImportSummary, SkippedRow } from "./types.ts";

interface ImportResultPanelProps {
  summary: ImportSummary;
  /** Server-side skips. Omit or pass empty to show the tally alone. */
  skipped: readonly SkippedRow[] | undefined;
}

export function ImportResultPanel({ summary, skipped }: ImportResultPanelProps) {
  return (
    <div className="mt-4 space-y-3">
      <p className="text-sm">
        <strong className="text-ok">Imported {summary.imported}</strong>
        {summary.skipped > 0 ? (
          <>
            {" · "}
            <strong className="text-muted">Skipped {summary.skipped}</strong>
          </>
        ) : null}
        .
      </p>
      {skipped?.length ? (
        <div className="max-h-48 overflow-auto rounded-sm border border-line">
          <table className="w-full text-xs">
            <thead className="bg-bg text-muted">
              <tr>
                <th className="px-2 py-1 text-left">Row</th>
                <th className="px-2 py-1 text-left">Reason</th>
                <th className="px-2 py-1 text-left">Message</th>
              </tr>
            </thead>
            <tbody>
              {skipped.map((s) => (
                <tr key={s.rowIndex} className="border-t border-line">
                  {/* +2 → 1-based, and past the header row, so the number
                      matches what the user sees in their spreadsheet. */}
                  <td className="px-2 py-1 text-muted">{s.rowIndex + 2}</td>
                  <td className="px-2 py-1">{s.reason.replace(/_/g, " ")}</td>
                  <td className="px-2 py-1">{s.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
